import React, { useState } from "react";
import Select, { components } from "react-select";
import FieldError from "./FieldError";
import Tooltip from "../../utils/tooltip";
import "../../App.css";

const CustomDropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path
        d="M6 8l4 4 4-4"
        fill="none"
        stroke="var(--textSecondary)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </components.DropdownIndicator>
);

function CompanySection({
  language,
  company,
  companyOptions,
  handleCompanyChange,
  createCompany,
  formErrors,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyError, setNewCompanyError] = useState("");
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);

  const t = {
    EN: {
      company: "Company",
      placeholder: "Select your company",
      newCompany: "New Company",
      modalTitle: "New Company",
      inputLabel: "Company name",
      inputPlaceholder: "Enter company name",
      cancel: "Cancel",
      create: "Create",
      required: "Company name is required.",
      duplicate: "This company already exists.",
      error: "Could not create the company. Try again.",
      noOptions: "No companies found",
      tooltip:
        'Search for and select your company in the dropdown. If it does not exist, you can create it with the "New Company" button. Once created, it will be selected automatically.',
    },
    ES: {
      company: "Empresa",
      placeholder: "Seleccioná tu empresa",
      newCompany: "Nueva Empresa",
      modalTitle: "Nueva Empresa",
      inputLabel: "Nombre de la empresa",
      inputPlaceholder: "Ingresá el nombre de la empresa",
      cancel: "Cancelar",
      create: "Crear",
      required: "El nombre de la empresa es obligatorio.",
      duplicate: "Esta empresa ya existe.",
      error: "No se pudo crear la empresa. Intentá nuevamente.",
      noOptions: "No se encontraron empresas",
      tooltip:
        'Buscá y seleccioná tu empresa en el desplegable. Si no existe, podés crearla desde el botón "Nueva Empresa". Una vez creada se seleccionará automáticamente.',
    },
  };

  const copy = t[language];

  const closeModal = () => {
    setIsModalOpen(false);
    setNewCompanyName("");
    setNewCompanyError("");
    setIsCreatingCompany(false);
  };

  const handleCreateCompany = async () => {
    const trimmedName = newCompanyName.trim();

    if (!trimmedName) {
      setNewCompanyError(copy.required);
      return;
    }

    const alreadyExists = companyOptions.some(
      (option) => option.label.toLowerCase() === trimmedName.toLowerCase()
    );

    if (alreadyExists) {
      setNewCompanyError(copy.duplicate);
      return;
    }

    try {
      setIsCreatingCompany(true);
      setNewCompanyError("");

      const created = await createCompany(trimmedName);

      if (!created) {
        setNewCompanyError(copy.error);
        return;
      }

      closeModal();
    } catch {
      setNewCompanyError(copy.error);
    } finally {
      setIsCreatingCompany(false);
    }
  };

  return (
    <div className="companyContainer">
      <label>
        <div>{copy.company} <b>*</b></div>
        <Tooltip text={copy.tooltip} />
      </label>

      <div className="companySelectRow">
        <Select
          unstyled
          components={{ DropdownIndicator: CustomDropdownIndicator }}
          isClearable
          value={companyOptions.find((c) => c.value === company) || null}
          onChange={handleCompanyChange}
          options={companyOptions}
          placeholder={copy.placeholder}
          noOptionsMessage={() => copy.noOptions}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={{
            control: (base) => ({
              ...base,
              alignItems: "center",
              border: "1px solid var(--borderColor)",
              background: "var(--bgSurface)",
              borderRadius: "5px",
              boxSizing: "border-box",
              boxShadow: "none",
              color: "var(--textSecondary)",
              fontSize: "14px",
              height: "37px",
              minHeight: "37px",
              paddingLeft: "10px",
              width: "100%",
            }),
            indicatorsContainer: (base) => ({
              ...base,
              height: "35px",
            }),
            valueContainer: (base) => ({
              ...base,
              height: "35px",
              overflow: "hidden",
              padding: "0 8px 0 0",
            }),
            input: (base) => ({
              ...base,
              color: "var(--textPrimary)",
              fontSize: "14px",
              margin: 0,
              padding: 0,
            }),
            placeholder: (base) => ({
              ...base,
              color: "var(--textSecondary)",
              fontSize: "14px",
            }),
            singleValue: (base) => ({
              ...base,
              color: "var(--textPrimary)",
              fontSize: "14px",
            }),
            menu: (base) => ({
              ...base,
              backgroundColor: "var(--bgSurface)",
              border: "1px solid var(--borderColor)",
              borderRadius: "5px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
              overflow: "hidden",
              zIndex: 9999,
            }),
            menuList: (base) => ({
              ...base,
              padding: 0,
              backgroundColor: "var(--bgSurface)",
            }),
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isFocused
              ? "var(--hoverSoft)"
              : "var(--bgSurface)",
              color: "var(--textPrimary)",
              fontSize: "14px",
              padding: "10px 12px",
              cursor: "pointer",
            }),
            noOptionsMessage: (base) => ({
              ...base,
              color: "var(--textSecondary)",
              fontSize: "14px",
              padding: "10px 12px",
              backgroundColor: "var(--bgSurface)",
            }),
          }}
        />

        <button
          type="button"
          className="newCompanyButton"
          onClick={() => setIsModalOpen(true)}
        >
          {copy.newCompany}
        </button>
      </div>

      <FieldError error={formErrors.company} />

      {isModalOpen && (
        <div className="companyModalOverlay" role="presentation">
          <div
            className="companyModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-company-title"
          >
            <h2 id="new-company-title">{copy.modalTitle}</h2>

            <label htmlFor="new-company-name">{copy.inputLabel}</label>
            <input
              id="new-company-name"
              type="text"
              value={newCompanyName}
              onChange={(event) => {
                setNewCompanyName(event.target.value);
                setNewCompanyError("");
              }}
              placeholder={copy.inputPlaceholder}
              autoComplete="off"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleCreateCompany();
                }
              }}
              autoFocus
            />

            <FieldError error={newCompanyError} />

            <div className="companyModalActions">
              <button type="button" className="btnPrint" onClick={closeModal}>
                {copy.cancel}
              </button>
              <button
                type="button"
                disabled={isCreatingCompany}
                onClick={handleCreateCompany}
              >
                {copy.create}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanySection;
